import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { usePlotArea } from 'recharts';
import {
  activeIndexOf,
  nearestIndexFromX,
  scrubReducer,
  type ChartScale,
  type PlotSpan,
  type ScrubEvent,
  type ScrubState,
} from '@/lib/chart-scrub';

export interface ChartScrubOverlayProps {
  count: number;
  scale: ChartScale;
  plotArea: PlotSpan | null;
  /** Row under the finger; null on release/cancel. */
  onIndexChange: (index: number | null) => void;
  /** Tap without drag. */
  onTap?: (index: number) => void;
}

/**
 * Transparent pointer layer over a chart (< 768px, AD-021). The chart never
 * receives the pointer: this layer computes the row under the finger and
 * reports it. `touch-action: pan-y` keeps vertical swipes scrolling the page
 * (the browser then sends `pointercancel`, which clears the reading).
 */
export function ChartScrubOverlay({
  count,
  scale,
  plotArea,
  onIndexChange,
  onTap,
}: ChartScrubOverlayProps) {
  const state = useRef<ScrubState>({ phase: 'idle' });
  const lastIndex = useRef<number | null>(null);
  const [active, setActive] = useState(false);

  const indexAt = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!plotArea) return null;
    const x = event.clientX - event.currentTarget.getBoundingClientRect().left;
    return nearestIndexFromX(x, plotArea, count, scale);
  };

  const dispatch = (event: ScrubEvent) => {
    const result = scrubReducer(state.current, event);
    state.current = result.state;
    setActive(result.state.phase !== 'idle');
    const index = activeIndexOf(result.state);
    if (index !== lastIndex.current) {
      lastIndex.current = index;
      onIndexChange(index);
    }
    if (result.tapIndex !== null) onTap?.(result.tapIndex);
  };

  const release = (event: ScrubEvent) => {
    if (state.current.phase !== 'idle') dispatch(event);
  };

  return (
    <div
      aria-hidden
      data-scrub-active={active ? 'true' : 'false'}
      className="absolute inset-0 z-10"
      style={{ touchAction: 'pan-y' }}
      onPointerDown={(event) => {
        if (!plotArea) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dispatch({
          type: 'down',
          x: event.clientX,
          at: event.timeStamp,
          index: indexAt(event),
        });
      }}
      onPointerMove={(event) => {
        if (state.current.phase === 'idle') return;
        dispatch({ type: 'move', x: event.clientX, index: indexAt(event) });
      }}
      onPointerUp={(event) => release({ type: 'up', at: event.timeStamp })}
      onPointerCancel={() => release({ type: 'cancel' })}
      onLostPointerCapture={() => release({ type: 'cancel' })}
    />
  );
}

/**
 * Chart child that reports the plot area (x and width, px relative to the
 * chart wrapper) so the overlay can map a finger to a row. Renders nothing.
 */
export function ChartPlotAreaReporter({
  onChange,
}: {
  onChange: (area: PlotSpan | null) => void;
}) {
  const area = usePlotArea();
  const left = area?.x;
  const width = area?.width;

  useEffect(() => {
    onChange(
      left === undefined || width === undefined ? null : { left, width }
    );
  }, [left, width, onChange]);

  return null;
}
